import cn from 'classnames'
import React from 'react'

import { SiteDataContext } from '../../components'
import { BILLING_TIME_ANNUALLY } from '../../components/blocks/pricingCards/pricingCards'
import RichText from '../../components/rich-text'
import { getMicroCopy } from '../../utils/microcopy'
import { getUrl } from '../../utils/urls'
import { ButtonNew, IconNew } from '../index'
import Image from 'next/image'
import { BUTTON_VARIANT } from '../ButtonNew/ButtonNew'
import { useNavigationData } from '@/src/context/NavigationContext'

const PricingCard = ({ card, billingTime, microCopy, slugPrefix, showMore, toggleShowMore }) => {
  const { market, isTextRtl, lang } = React.useContext(SiteDataContext)
  const { navigationItems } = useNavigationData()

  const bulletPoints =
    card?.bulletPoints && Array.isArray(card?.bulletPoints) ? card.bulletPoints : []

  const handleShowMore = () => {
    toggleShowMore(!showMore)
  }

  return (
    <article
      className={cn('c-pricing-card', {
        'c-pricing-card--dark': card.theme === 'dark',
        'c-pricing-card--rlt': isTextRtl
      })}
      dir={isTextRtl ? 'rtl' : 'ltr'}
    >
      <div className="c-pricing-card__price-container">
        <div className="c-pricing-card__title-container">
          {card.image ? (
            <Image
              src={card.image.url}
              alt={card.image.title || card.alt}
              width={32}
              height={32}
              style={{
                objectFit: 'contain'
              }}
            />
          ) : card.icon ? (
            <IconNew icon={card.icon} width={32} height={32} />
          ) : null}
          <h2 className="c-pricing-card__header">{card.title}</h2>
        </div>
        {card.description && (
          <div className="c-pricing-card__description">
            <RichText data={card.description} />
          </div>
        )}
        {card.displayTitle ? (
          <p className="c-pricing-card__display-title">{card.displayTitle}</p>
        ) : card.price ? (
          <>
            <p className="c-pricing-card__price">
              {getMicroCopy({ key: 'from', fallback: 'From', data: microCopy[lang] })}{' '}
              <span className="c-pricing-card__price-text">
                {market?.currencySymbol ? market.currencySymbol : '$'}
                {card.price}
              </span>
              <span className="c-pricing-card__billing-time">/</span>
              <span className="c-pricing-card__billing-time">
                {getMicroCopy({ key: 'month', data: microCopy[lang] })}
              </span>
            </p>
          </>
        ) : null}

        {card.action && (
          <ButtonNew
            size={'md'}
            variant={card.theme === 'dark' ? BUTTON_VARIANT.GREEN : BUTTON_VARIANT.PRIMARY}
            fullWidth
            classes="c-pricing-card__action"
            url={getUrl({
              navItem: card.action,
              slugPrefix: slugPrefix,
              navigationItems: navigationItems
            })}
          >
            {card.action.title || ''}
          </ButtonNew>
        )}

        {card.totalAnnualPrice && (
          <p
            className={cn('c-pricing-card__annual-footnote', {
              'c-pricing-card__annual-footnote--visible': billingTime === BILLING_TIME_ANNUALLY
            })}
          >
            {market?.currencySymbol ? market.currencySymbol : '$'}
            {card?.totalAnnualPrice}{' '}
            {getMicroCopy({ key: 'billedAnnually', data: microCopy[lang] })}
          </p>
        )}
      </div>

      <hr />

      {bulletPoints.length > 0 ? (
        <div className={'c-pricing-card__bullet-points l-grid__col:12/12'}>
          <div className="c-pricing-card__bullet-point c-pricing-card__bullet-point--label">
            <div className="c-pricing-card__bullet-point__text">
              <p className="c-pricing-card__bullet-point__title">
                {getMicroCopy({ key: 'includes', data: microCopy[lang] })}:
              </p>
            </div>
          </div>
          {bulletPoints.slice(0, showMore ? bulletPoints.length : 4).map((bulletPoint, i) => (
            <div
              key={`${card.title}-${bulletPoint.id}-${i}`}
              className="c-pricing-card__bullet-point"
            >
              {bulletPoint.icon ? <IconNew name={bulletPoint.icon} /> : null}
              <div className="c-pricing-card__bullet-point__text">
                <p className="c-pricing-card__bullet-point__title">{bulletPoint.title}</p>
              </div>
            </div>
          ))}
          {bulletPoints.length > 4 ? (
            <div className="c-pricing-card__show-toggle">
              <ButtonNew
                variant={BUTTON_VARIANT.TERTIARY}
                onClick={handleShowMore}
                classes={'u-padding-0'}
              >
                {showMore
                  ? getMicroCopy({ key: 'showLess', data: microCopy[lang] })
                  : getMicroCopy({ key: 'showMore', data: microCopy[lang] })}

                <IconNew name={showMore ? 'tabler-chevron-up' : 'tabler-chevron-down'} />
              </ButtonNew>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}

export default PricingCard
